import React, { useState } from 'react';
import { db } from './firebaseconfig';
import { collection, query, where, getDocs } from 'firebase/firestore';

const cardStyle = {
  border: '1px solid #ddd',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '16px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  backgroundColor: '#fff',
};

const headerStyle = {
  marginBottom: '8px',
  color: '#333',
};

const labelStyle = {
  fontWeight: '600',
  color: '#555',
};

const errorStyle = {
  color: 'red',
  marginTop: '12px',
};

const OrderLookup = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderError, setOrderError] = useState('');
  
  const [salonId, setSalonId] = useState('');
  const [salonOrders, setSalonOrders] = useState([]);
  const [totalSalonPrice, setTotalSalonPrice] = useState(0);
  const [salonError, setSalonError] = useState('');

  // In-memory caches
  const [orderCache, setOrderCache] = useState({});
  const [salonCache, setSalonCache] = useState({});

  // Search for an order by its orderNumber with cache
  const handleOrderSearch = async () => {
    setOrderError('');
    setOrderDetails(null);

    if (!orderNumber) {
      setOrderError('Please enter an order number.');
      return;
    }
    const orderNum = parseInt(orderNumber, 10);

    // Check cache first
    if (orderCache[orderNum]) {
      setOrderDetails(orderCache[orderNum]);
      return;
    }

    const q = query(
      collection(db, 'orders'),
      where('orderNumber', '==', orderNum)
    );
    const querySnapshot = await getDocs(q);
    let foundOrder = null;
    querySnapshot.forEach((doc) => {
      foundOrder = doc.data();
    });
    if (foundOrder) {
      setOrderDetails(foundOrder);
      setOrderCache((prev) => ({ ...prev, [orderNum]: foundOrder }));
    } else {
      setOrderError('Sorry, order number not found.');
    }
  };

  // Filter orders by salon id and calculate total price with cache
  const handleSalonFilter = async () => {
    setSalonError('');
    setSalonOrders([]);
    setTotalSalonPrice(0);

    if (!salonId) {
      setSalonError('Please enter a salon ID.');
      return;
    }
    // Check cache first
    if (salonCache[salonId]) {
      const { orders, total } = salonCache[salonId];
      setSalonOrders(orders);
      setTotalSalonPrice(total);
      if (orders.length === 0) {
        setSalonError('Sorry, no orders found for that salon ID.');
      }
      return;
    }

    const q = query(
      collection(db, 'orders'),
      where('saloonId', '==', salonId)
    );
    const querySnapshot = await getDocs(q);
    const orders = [];
    let total = 0;
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      orders.push(data);
      total += data.totalPrice;
    });
    setSalonOrders(orders);
    setTotalSalonPrice(total);
    setSalonCache((prev) => ({ ...prev, [salonId]: { orders, total } }));
    if (orders.length === 0) {
      setSalonError('Sorry, no orders found for that salon ID.');
    }
  };

  // Render order items in a clean UI
  const renderOrderItems = (orderItems) => {
    return orderItems.map((item, idx) => (
      <div key={idx} style={{ marginBottom: '8px', paddingLeft: '8px' }}>
        <div>
          <span style={labelStyle}>Item Name:</span> {item.name}
        </div>
        <div>
          <span style={labelStyle}>Price:</span> ₹{item.price}
        </div>
        <div>
          <span style={labelStyle}>Quantity:</span> {item.quantity}
        </div>
      </div>
    ));
  };

  // Render a card for order details
  const renderOrderCard = (order) => {
    return (
      <div style={cardStyle}>
        <h3 style={headerStyle}>Order Number: {order.orderNumber}</h3>
        <div>
          <span style={labelStyle}>Appointment Date:</span> {order.appointmentDate}
        </div>
        <div>
          <span style={labelStyle}>Appointment Time:</span> {order.appointmentTime}
        </div>
        <div>
          <span style={labelStyle}>Discount:</span> {order.discount}%
        </div>
        <div>
          <span style={labelStyle}>GST Amount:</span> ₹{order.gstAmount}
        </div>
        <div>
          <span style={labelStyle}>Payment Method:</span> {order.paymentMethod}
        </div>
        <div>
          <span style={labelStyle}>Phone Number:</span> {order.phoneNumber}
        </div>
        <div>
          <span style={labelStyle}>Status:</span> {order.status}
        </div>
        <div style={{ marginTop: '12px' }}>
          <strong>Order Items:</strong>
          {order.orderItems && order.orderItems.length > 0 ? (
            <div>{renderOrderItems(order.orderItems)}</div>
          ) : (
            <div>No Items</div>
          )}
        </div>
        <div style={{ marginTop: '12px' }}>
          <span style={labelStyle}>Total Price:</span> ₹{order.totalPrice}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', backgroundColor: '#f7f7f7' }}>
      <h2 style={{ marginBottom: '16px', color: '#333' }}>Order Lookup</h2>
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ color: '#444' }}>Lookup Order by Order Number</h3>
        <input
          type="text"
          placeholder="Enter Order Number"
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          style={{ padding: '8px', marginRight: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
        <button
          onClick={handleOrderSearch}
          style={{
            padding: '8px 16px',
            borderRadius: '4px',
            backgroundColor: '#007bff',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Search Order
        </button>
        {orderError && <div style={errorStyle}>{orderError}</div>}
        {orderDetails && (
          <div style={{ marginTop: '20px' }}>
            {renderOrderCard(orderDetails)}
          </div>
        )}
      </div>

      <hr style={{ margin: '40px 0', border: 'none', borderTop: '1px solid #ccc' }} />

      <div>
        <h3 style={{ color: '#444' }}>Filter Orders by Salon ID</h3>
        <input
          type="text"
          placeholder="Enter Salon ID"
          value={salonId}
          onChange={(e) => setSalonId(e.target.value)}
          style={{ padding: '8px', marginRight: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
        <button
          onClick={handleSalonFilter}
          style={{
            padding: '8px 16px',
            borderRadius: '4px',
            backgroundColor: '#28a745',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Filter Orders
        </button>
        {salonError && <div style={errorStyle}>{salonError}</div>}
        {salonOrders.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h3 style={{ color: '#333' }}>Orders for Salon ID: {salonId}</h3>
            {salonOrders.map((order, idx) => (
              <div key={idx}>{renderOrderCard(order)}</div>
            ))}
            <h4 style={{ color: '#333', marginTop: '16px' }}>
              Total Price for Salon ID {salonId}: ₹{totalSalonPrice.toFixed(2)}
            </h4>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderLookup;
